// Confirmation and prompt dialogs of the app, in place of the browser's `window.confirm` / `window.prompt`.
// They are called from anywhere (components, handlers, helpers) and answer with a promise:
//   if (!(await confirmDialog({ title: "Anular", message: "...", tone: "danger" }))) return;
//   const reason = await promptDialog({ title: "Anular", label: "Motivo" }); // null when cancelled
// The dialog itself is drawn by <DialogHost />, mounted once at the root of the app. Without it (a test, a
// screen outside the app) the browser's own dialogs are used, so nothing is left unanswered.

export type DialogTone = "primary" | "danger" | "warning";

export interface ConfirmOptions {
  title?: string;
  // Line breaks are kept.
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: DialogTone;
}

export interface PromptOptions extends ConfirmOptions {
  label?: string;
  defaultValue?: string;
  placeholder?: string;
}

export type DialogRequest =
  | { kind: "confirm"; options: ConfirmOptions; resolve: (value: boolean) => void }
  | { kind: "prompt"; options: PromptOptions; resolve: (value: string | null) => void };

let host: ((request: DialogRequest) => void) | null = null;

/** Called by <DialogHost />: it is the one that draws the dialogs. Returns how to unregister it. */
export const registerDialogHost = (handler: (request: DialogRequest) => void) => {
  host = handler;

  return () => {
    if (host === handler) host = null;
  };
};

export const confirmDialog = (options: ConfirmOptions | string): Promise<boolean> => {
  const normalized = typeof options === "string" ? { message: options } : options;

  if (!host) return Promise.resolve(window.confirm(normalized.message));

  const show = host;

  return new Promise((resolve) => show({ kind: "confirm", options: normalized, resolve }));
};

export const promptDialog = (options: PromptOptions): Promise<string | null> => {
  if (!host) return Promise.resolve(window.prompt(options.message, options.defaultValue ?? ""));

  const show = host;

  return new Promise((resolve) => show({ kind: "prompt", options, resolve }));
};
